import { useTranslate } from '@ur-solutions/react-hooks'
import {
  Button as BaseButton,
  Input,
  ModuleTitle,
} from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PlainLink } from '@/components_deprecated/Link'

import { PermissionsRequired } from '@/containers/permission-containers'
import React, { useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import styled from 'styled-components'
import {
  useAdmin,
  useDebounce,
  useUser,
  useUserLeaderGroups,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { ThreadsTable } from '../components'
import type { Tab} from '../../../components_deprecated/Tabs';
import { TabPane, Tabs } from '../../../components_deprecated/Tabs'
import { PageType } from '@/modules/common/consts'
import { useInfoModal } from '@/utils/hooks/useInfoModal/useInfoModal'
import { ForumTabKey } from '../types'
import { LeaderGroupSelect } from '@/components_deprecated/Select/LeaderGroupSelect'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
`
const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: column;
  gap: 1rem;

  margin-bottom: 1rem;
  padding: 1rem;
`
const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`
const Button = styled(BaseButton)`
  ${props => props.theme.media.mobile} {
    padding: 0 3rem 0 1rem;
  }
`

export const Forum: React.FC = () => {
  const translations = useTranslate({
    forum: 'common.forum',
    searchInForum: 'forum.search',
    newThread: 'forum.new-thread',
    chooseGroup: 'common.choose-group',

    general: 'common.general',
    direct: 'common.direct',
  })

  const tabs = useMemo<[Tab, Tab]>(
    () => [
      { key: ForumTabKey.GENERAL, text: translations.general },
      { key: ForumTabKey.DIRECT, text: translations.direct },
    ],
    [translations.general, translations.direct]
  )

  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<string>(
    localStorage.getItem('forumSort') || '-createdAt'
  )
  const [selectedLeaderGroup, setSelectedLeaderGroup] = useState<string | null>(
    null
  )

  const me = useUser()
  const isAdmin = useAdmin()
  const { hasAccess, myLeaderGroups, groupsLoading } = useUserLeaderGroups(
    me.id,
    isAdmin
  )

  const getInitialTab = () => {
    const savedTabKey = localStorage.getItem('forumActiveTab')
    if (savedTabKey === ForumTabKey.DIRECT) {
      return tabs[1]
    }
    return tabs[0]
  }

  const [activeTab, setActiveTab] = useState<Tab>(getInitialTab())

  const debouncedQuery = useDebounce(query)

  const { InfoModal, infoContent, InfoButton } = useInfoModal(PageType.FORUM)

  useEffect(() => {
    localStorage.setItem('forumSort', sort || '-createdAt')
  }, [sort])

  useEffect(() => {
    localStorage.setItem('forumActiveTab', activeTab.key)
  }, [activeTab.key])

  const createLink =
    activeTab.key === ForumTabKey.GENERAL
      ? '/forum/create'
      : '/forum/create-direct'

  return (
    <Wrapper>
      {isMobile && (
        <MobileHeader>
          <ModuleTitle inlineHeader>
            {translations.forum}
            {infoContent && <InfoButton />}
          </ModuleTitle>

          <PermissionsRequired permissions={PERMISSIONS.forum.add.thread}>
            <PlainLink to={createLink}>
              <Button
                iconLeftProps={{ icon: 'plus', margin: '0' }}
                height="42px"
                padding="0"
                fontSize="0.8em"
              >
                {translations.newThread}
              </Button>
            </PlainLink>
          </PermissionsRequired>
        </MobileHeader>
      )}

      {!isMobile && (
        <ModuleTitle>
          {translations.forum}
          {infoContent && <InfoButton />}
        </ModuleTitle>
      )}
      <Tabs
        activeTab={activeTab}
        tabs={tabs}
        onChange={tab => setActiveTab(tab)}
      />
      <TabPane active={activeTab.key === ForumTabKey.GENERAL}>
        <Controls>
          <Input
            value={query}
            type="search"
            placeholder={translations.searchInForum}
            onChange={setQuery}
          />
          {!isMobile && (
            <PermissionsRequired permissions={PERMISSIONS.forum.add.thread}>
              <PlainLink to="/forum/create">
                <Button iconLeftProps={{ icon: 'plus' }}>
                  {translations.newThread}
                </Button>
              </PlainLink>
            </PermissionsRequired>
          )}
        </Controls>
        <ThreadsTable
          query={debouncedQuery}
          sort={sort}
          onSort={setSort}
          leaderGroupId={selectedLeaderGroup}
        />
      </TabPane>

      <TabPane active={activeTab.key === ForumTabKey.DIRECT}>
        <Controls>
          <Input
            value={query}
            type="search"
            placeholder={translations.searchInForum}
            onChange={setQuery}
          />
          {hasAccess && (
            <LeaderGroupSelect
              value={selectedLeaderGroup}
              loading={groupsLoading}
              options={
                myLeaderGroups?.map(group => ({
                  label: group.name,
                  value: group.id,
                })) ?? []
              }
              fullWidth
              grayed={true}
              onChange={value => setSelectedLeaderGroup(value)}
              placeholder={translations.chooseGroup}
              nullable
            />
          )}
          {!isMobile && (
            <PermissionsRequired permissions={PERMISSIONS.forum.add.thread}>
              <PlainLink to="/forum/create-direct">
                <Button iconLeftProps={{ icon: 'plus' }}>
                  {translations.newThread}
                </Button>
              </PlainLink>
            </PermissionsRequired>
          )}
        </Controls>
        <ThreadsTable
          query={debouncedQuery}
          sort={sort}
          onSort={setSort}
          isDirectMessage
          leaderGroupId={selectedLeaderGroup}
        />
      </TabPane>
      {infoContent && <InfoModal />}
    </Wrapper>
  )
}
